import { useState, useEffect, useRef } from 'react'
import { useGetContentImageQuery, useViewContentQuery } from '../services/contentApi'

let maxConcurrentRequests = 5
let currentRequests = 0
const maxRequestsPerSecond = 20
let requestsThisSecond = 0
let lastRequestTime = Date.now()
const requestQueue: (() => void)[] = []

export const resetImageLoadingQueue = (max?: number) => {
  if (max !== undefined) {
    maxConcurrentRequests = max
  }
  currentRequests = 0
  requestsThisSecond = 0
  lastRequestTime = Date.now()
  requestQueue.length = 0
}

const processQueue = () => {
  const now = Date.now()
  // Reset counter if a second has passed
  if (now - lastRequestTime >= 1000) {
    requestsThisSecond = 0
    lastRequestTime = now
  }

  // Only process queue if we haven't hit the rate limit
  while (
    requestQueue.length > 0 &&
    currentRequests < maxConcurrentRequests &&
    requestsThisSecond < maxRequestsPerSecond
  ) {
    const nextRequest = requestQueue.shift()
    if (nextRequest) {
      currentRequests++
      requestsThisSecond++
      nextRequest()
    }
  }

  // Schedule next queue processing if there are items in queue
  if (requestQueue.length > 0) {
    setTimeout(processQueue, 50) // Check queue every 50ms
  }
}

export const useContentImage = ({
  contentId,
  projectId,
  datasetId,
}: {
  contentId: string
  projectId: string
  datasetId?: string
}) => {
  const [base64Image, setBase64Image] = useState<string | null>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [cacheTimestamp, setCacheTimestamp] = useState<number>(Date.now())
  const imageRef = useRef(null)
  const isMounted = useRef(true)

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  // Reset state when contentId changes or on reset event
  useEffect(() => {
    const handleReset = () => {
      if (!isMounted.current) return
      setBase64Image(null)
      setIsVisible(false)
      setCacheTimestamp(Date.now())
    }

    const handleAnnotationUpdate = (event: CustomEvent) => {
      if (!isMounted.current) return
      const { contentId: updatedContentId } = event.detail || {}
      // Force refresh if this is the updated content or reset all images
      if (!updatedContentId || updatedContentId === contentId) {
        setBase64Image(null)
        setCacheTimestamp(Date.now())
      }
    }

    handleReset()
    window.addEventListener('reset-image-loading', handleReset)
    window.addEventListener('annotation-updated', handleAnnotationUpdate as EventListener)

    return () => {
      window.removeEventListener('reset-image-loading', handleReset)
      window.removeEventListener('annotation-updated', handleAnnotationUpdate as EventListener)
    }
  }, [contentId])

  // Setup intersection observer with larger root margin for earlier loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!isMounted.current) return
        setIsVisible(entry.isIntersecting)
      },
      { rootMargin: '10px', threshold: 0.3 }
    )

    if (imageRef.current) {
      observer.observe(imageRef.current)
    }

    return () => observer.disconnect()
  }, [])

  const {
    data: imageUrl,
    error: imageError,
    isLoading: imageLoading,
  } = useGetContentImageQuery(
    {
      ContentID: contentId,
      project_id: projectId,
      dataset_id: datasetId,
      _t: cacheTimestamp,
    },
    { skip: !contentId || !isVisible }
  )

  const { data: contentData } = useViewContentQuery(
    {
      ContentID: contentId || '',
      project_id: projectId || '',
      dataset_id: datasetId || '',
    },
    {
      skip: !contentId || !isVisible,
    }
  )

  // Simple direct image loading
  useEffect(() => {
    if (!imageUrl || !isVisible || !isMounted.current) return

    const loadImage = async () => {
      try {
        // Check visibility again before starting download
        if (!isVisible || !isMounted.current) {
          return
        }

        const response = await fetch(imageUrl)
        // Skip processing if image is no longer visible
        if (!isVisible || !isMounted.current) {
          return
        }

        const blob = await response.blob()
        if (!isVisible || !isMounted.current) {
          return
        }

        const reader = new FileReader()
        reader.onloadend = () => {
          if (!isVisible || !isMounted.current) return
          setBase64Image(reader.result as string)
        }
        reader.readAsDataURL(blob)
      } catch (error) {
        console.error('Error loading image:', error)
      } finally {
        currentRequests--
        processQueue()
      }
    }

    const queueOrExecuteRequest = () => {
      // Only queue/execute if still visible
      if (!isVisible) {
        return
      }

      if (currentRequests < maxConcurrentRequests) {
        currentRequests++
        loadImage()
      } else {
        requestQueue.push(loadImage)
      }
    }

    queueOrExecuteRequest()
  }, [imageUrl, isVisible])

  return { base64Image, imageError, imageLoading, imageRef, contentData }
}
